import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";

import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { setJoinCampTnCChecked } from "@/features/campaigns/shared/store/campaignSlice";
import { USER_ROLE } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

type PaymentReturnType =
  | "subscription"
  | "canada_subscription"
  | "cad_subscription"
  | "pad_subscription"
  | "join_campaign"
  | "rrd_join_campaign"
  | "national_plus_up_join_campaign"
  | "boost_campaign"
  | "extend_campaign";
type PaymentReturnPageProps = {
  type: PaymentReturnType;
};
type PaymentReturnRecord = Record<string, any>;
type ThankYouNavigationState = PaymentReturnRecord & { from?: string };
type GlobalContextValue = {
  loggedInUserRole?: string;
};
type Loader = (_show: boolean) => void;
type Dispatch = (_action: any) => any;

/**
 * Common component to handle all payment return flows from Stripe.
 * @param {Object} props
 * @param {'subscription' | 'canada_subscription' | 'cad_subscription' | 'pad_subscription' | 'join_campaign' | 'rrd_join_campaign' | 'national_plus_up_join_campaign' | 'boost_campaign' | 'extend_campaign'} props.type - The type of payment flow.
 */
function PaymentReturnPage({ type }: PaymentReturnPageProps) {
  const [resData, setResData] = useState<PaymentReturnRecord | null>(null);
  const [error, setError] = useState<unknown>(null);
  const status = resData?.status;

  const loader = useContext(LoaderContext) as Loader;
  const { loggedInUserRole } = useContext(GlobalContext) as GlobalContextValue;
  const isParticipant = loggedInUserRole === USER_ROLE.PARTICIPANT.NAME;

  const isConfirming = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch() as Dispatch;

  const { success, sessionId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      success: params.get("success"),
      sessionId: params.get("session_id"),
    };
  }, []);

  useEffect(() => {
    logger.info("Verifying payment return", { type });

    async function confirmPayment(sessionId: string) {
      loader(true);
      try {
        let res: { data?: PaymentReturnRecord } | undefined;
        switch (type) {
          case "subscription":
            res =
              await stripeMicroService.subscriptionPaymentConfirm(sessionId);
            break;
          case "canada_subscription":
            res =
              await stripeMicroService.canadaSubscriptionPaymentConfirm(
                sessionId
              );
            break;
          case "cad_subscription":
            res =
              await stripeMicroService.cadSubscriptionPaymentConfirm(sessionId);
            break;
          case "pad_subscription":
            res =
              await stripeMicroService.padSubscriptionPaymentConfirm(sessionId);
            break;
          case "join_campaign":
          case "rrd_join_campaign":
          case "national_plus_up_join_campaign":
            res =
              await stripeMicroService.joinCampaignPaymentConfirm(sessionId);
            // Handle specific logic for join campaign
            dispatch(setJoinCampTnCChecked(true));
            break;
          case "boost_campaign":
            res =
              await stripeMicroService.boostCampaignPaymentConfirm(sessionId);
            break;
          case "extend_campaign":
            res =
              await stripeMicroService.extendCampaignPaymentConfirm(sessionId);
            break;
          default:
            throw new Error("Invalid payment type");
        }

        const data = res?.data?.result?.data || { status: "" };
        setResData(data);
      } catch (err) {
        setResData({ status: "" });
        setError(err);
        logger.error("Retrun page Error:", err);
      } finally {
        loader(false);
      }
    }

    if (
      success === "true" &&
      sessionId &&
      !isConfirming.current &&
      !resData &&
      !error
    ) {
      isConfirming.current = true;
      confirmPayment(sessionId);
    } else if (success === "false" && !resData) {
      setResData({ status: "failed" });
    }
  }, [type, loader, dispatch, success, sessionId, resData, error]);

  useEffect(() => {
    if (status && status === "complete") {
      let navigatePath = ROUTES.THANK_YOU;
      let stateData: ThankYouNavigationState = {};

      switch (type) {
        case "subscription":
        case "canada_subscription":
        case "cad_subscription":
        case "pad_subscription":
          const shouldRedirectToCustomizeAds =
            isParticipant &&
            (resData?.is_redirect ||
              resData?.worker_response?.instructions?.is_redirect);

          stateData = {
            from: "package_selection",
            sessionId: resData?.id,
            data: resData,
            is_redirect: shouldRedirectToCustomizeAds,
            campaign_id:
              resData?.campaign_id ||
              resData?.worker_response?.instructions?.campaign_id,
            stripe_checkout: true,
          };
          break;
        case "join_campaign":
          stateData = { from: "join_campaign_confirm" };
          break;
        case "rrd_join_campaign":
          stateData = { from: "rrd-join-campaign-confirm" };
          break;
        case "national_plus_up_join_campaign":
          stateData = { from: "national-plus-up-confirm" };
          break;
        case "boost_campaign":
          navigatePath = `${ROUTES.THANK_YOU}?type=adjust_budget`;
          stateData = { from: "boost_campaign_confirm" };
          break;
        case "extend_campaign":
          navigatePath = `${ROUTES.THANK_YOU}?type=extend_campaign`;
          stateData = { from: "extend_campaign" };
          break;
        default:
          break;
      }

      grantThankYouAccessForFlow({
        queryType: navigatePath.includes("?type=")
          ? new URL(navigatePath, window.location.origin).searchParams.get(
              "type"
            )
          : undefined,
        stateFrom: stateData.from,
      });
      navigate(navigatePath, { state: stateData, replace: true });
    }
  }, [status, type, resData, isParticipant, navigate]);

  if (status && status !== "complete") {
    return (
      <div className="text-center mt-5">
        <Typography variant="body1">Payment Status: {status}</Typography>
      </div>
    );
  }

  if (error || status === "") {
    return (
      <div
        style={{ height: "100vh", width: "100vw" }}
        className="d-flex-center flex-column"
      >
        <Typography variant="h6" color="error" gutterBottom>
          Something went wrong!
        </Typography>
        <Typography variant="body2" color="textSecondary">
          We couldn't process your payment. Please keep the reference ID below
          to share with support.
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Reference ID: {sessionId}
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", width: "100vw" }} className="d-flex-center">
      <div className="text-center">
        <div>
          <CircularProgress size={20} color="primary" />
        </div>
        <Typography variant="caption">Please wait...</Typography>
      </div>
    </div>
  );
}

export default PaymentReturnPage;
